import { createSlice } from '@reduxjs/toolkit';
import { login, getAffiliateStatus, logoutAuth } from '../../api/api_auth';

const affiliateSlice = createSlice({
    name: 'affiliate',
    initialState: {
        auth: null,
        loading: false,
        error: null,
        authChecked: false, // Flag to track if we've already checked auth
    },
    reducers: {
        setAuth: (state, action) => {
            // Set default waiter image if affiliate image is empty
            const affiliate = action.payload;
            if (affiliate && (!affiliate.image || affiliate.image === '' || affiliate.image === null)) {
                affiliate.image = 'https://cdn.pixabay.com/photo/2013/07/12/14/43/waiter-148663_1280.png';
            }
            state.auth = affiliate;
            state.authChecked = true;
        },
        setLoading: (state, action) => {
            state.loading = action.payload;
        },
        setError: (state, action) => {
            state.error = action.payload;
        },
        clearAuth: (state) => {
            state.auth = null;
            state.error = null;
            state.authChecked = true;
        },
        setAuthChecked: (state, action) => {
            state.authChecked = action.payload;
        },
        updateAuth: (state, action) => {
            if (state.auth) {
                state.auth = { ...state.auth, ...action.payload };
            }
        },
    },
});

export const { setAuth, setLoading, setError, clearAuth, setAuthChecked, updateAuth } = affiliateSlice.actions;

// Check affiliate's authentication status
export const checkAuthStatus = () => async (dispatch, getState) => {
    const state = getState();
    
    // Don't check if we're already loading or have already checked
    if (state.affiliate.loading || state.affiliate.authChecked) {
        return;
    }
    
    dispatch(setLoading(true));
    try {
        const response = await getAffiliateStatus();
        if (response.data.logged_in) {
            dispatch(setAuth(response.data.affiliate));
        } else {
            dispatch(clearAuth());
        }
    } catch (error) {
        console.error('Auth check failed:', error);
        dispatch(setError('Unable to authenticate'));
        dispatch(setAuthChecked(true)); // Mark as checked even on error
    } finally {
        dispatch(setLoading(false));
    }
};

// Login the affiliate and manage session with cookies
export const loginUser = (credentials) => async (dispatch) => {
    dispatch(setLoading(true));
    dispatch(setError(null)); // Clear any previous errors
    
    try {
        const response = await login(credentials);
        const { affiliate } = response.data;
        dispatch(setAuth(affiliate));
    } catch (error) {
        console.error('Login failed:', error);
        dispatch(setError('Login failed - please check your credentials'));
    } finally {
        dispatch(setLoading(false));
    }
};

// Logout the affiliate and clear session
export const logout = () => async (dispatch) => {
    dispatch(setLoading(true));
    try {
        await logoutAuth();
        dispatch(clearAuth());
        // Keep authChecked as true (set by clearAuth) - we know the auth status
    } catch (error) {
        console.error('Logout failed:', error);
        dispatch(setError('Logout failed'));
        // Still clear local auth even if API call fails
        dispatch(clearAuth());
    } finally {
        dispatch(setLoading(false));
    }
};

export default affiliateSlice.reducer;
